/**
 * Reading the details a refusal carries. The backend puts them in the RFC 7807
 * body's `args` member, which the ApiError keeps under `extensions`.
 */
import { isApiError } from "@linkhr/api-client/core";

/** The refusal's `args`, or an empty object. */
export function problemArgs(error: unknown): Record<string, unknown> {
  const args = isApiError(error) ? error.extensions?.args : undefined;
  return args !== null && typeof args === "object" ? (args as Record<string, unknown>) : {};
}

/** Where each stale refusal keeps the version the server now holds. */
const STALE_VERSION_KEY: Readonly<Record<string, string>> = {
  COMPANY_GRANT_STALE: "version",
  ACCT_DRAFT_STALE: "draftVersion",
  ACCT_BILL_STALE: "currentDraftVersion",
  ACCT_PAYMENT_STALE: "currentDraftVersion",
  CONCURRENT_MODIFICATION: "currentVersion",
  ACCT_SETTINGS_VERSION_CONFLICT: "version",
};

export interface Stale {
  /** Who saved it, when the server says. */
  by: string | null;
  at: string | null;
  /** The fields changed since the version sent, when the server lists them. */
  fields: string[] | null;
  /** The version now saved — what the next attempt must send. */
  version: number | null;
  /**
   * Why, when the server says: a payment being approved or released was EDITED, or its
   * figures moved with no save (BOOKS_MOVED); an approved payment was saved (APPROVED).
   */
  reason: string | null;
  /** The payment's content hash now (BOOKS_MOVED); null when the version moved (EDITED). */
  hash: string | null;
  /** The documents whose figures moved (BOOKS_MOVED), when the server names them. */
  documents: { id: string; number: string | null }[] | null;
}

/** No answer came back (offline, a gateway), so whether a keyed create happened is unknown. */
export const unanswered = (error: unknown): boolean =>
  !isApiError(error) || [0, 502, 503, 504].includes(error.status);

/** A non-empty string, or null. */
export const text = (value: unknown): string | null =>
  typeof value === "string" && value !== "" ? value : null;

/** A list arg's codes: its strings (`roles`), or its items' `code` (`[{id, code}]`); whatever arrives. */
export const codesIn = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.flatMap((item: unknown) => {
        const code =
          item !== null && typeof item === "object"
            ? text((item as { code?: unknown }).code)
            : text(item);
        return code !== null ? [code] : [];
      })
    : [];

/**
 * Someone else saved the record first: who, when, what changed and the version
 * to send next; null for any other refusal. Drafts and grants name
 * `savedBy`/`savedAt`, a bill or a payment being approved `changedBy`/`changedAt`,
 * an approved payment saved without clearing its approval `approvedBy`/`approvedAt`;
 * contacts and settings carry the version only.
 */
export function readStale(error: unknown): Stale | null {
  if (!isApiError(error)) return null;
  const versionKey = STALE_VERSION_KEY[error.errorCode];
  if (versionKey === undefined) return null;
  const args = problemArgs(error);
  const by = (args.savedBy ?? args.changedBy ?? args.approvedBy) as
    | { name?: unknown }
    | null
    | undefined;
  const at = args.savedAt ?? args.changedAt ?? args.approvedAt;
  const version = args[versionKey];
  const fields = Array.isArray(args.changedFields)
    ? args.changedFields.filter((field): field is string => typeof field === "string")
    : null;
  const documents = Array.isArray(args.documents)
    ? args.documents.flatMap((doc: unknown) => {
        const { id, number } = (doc ?? {}) as Record<string, unknown>;
        return typeof id === "string" ? [{ id, number: text(number) }] : [];
      })
    : null;
  return {
    by: text(by?.name),
    at: typeof at === "string" ? at : null,
    fields,
    version: typeof version === "number" ? version : null,
    reason: text(args.reason),
    hash: text(args.currentHash),
    documents,
  };
}
